import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge, urgencyTone } from "../components/ui/Badge";
import { DataState } from "../components/ui/DataState";
import type { Alert } from "../lib/types";

export default function Alerts() {
  const api = useApi();
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ alerts: Alert[] }>("/api/alerts")
      .then((r) => {
        setAlerts(r.alerts);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="container-page py-12">
      <header>
        <h1 className="text-3xl font-semibold text-white">Community alerts</h1>
        <p className="mt-2 text-slate-300">
          Only approved alerts appear here. Personal identifying information is
          removed before publication.
        </p>
      </header>

      <div className="mt-8">
        <DataState
          loading={loading}
          error={error}
          data={alerts}
          empty={!!alerts && alerts.length === 0}
          loadingMessage="Loading approved alerts…"
          emptyMessage="No active alerts right now. That's a good thing."
        >
          {(items) => (
            <div className="grid gap-5">
              {items.map((a) => (
                <Card key={a.id}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={urgencyTone(a.urgency)}>
                      {a.urgency} urgency
                    </Badge>
                    <Badge>{a.category.replace(/_/g, " ")}</Badge>
                    {a.neighborhood && <Badge>{a.neighborhood}</Badge>}
                    <Badge tone="success">{a.status}</Badge>
                  </div>
                  <CardTitle className="mt-3 text-lg">{a.title}</CardTitle>
                  <p className="mt-2 text-slate-300">{a.public_summary}</p>
                  {a.recommended_action && (
                    <p className="mt-3 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2 text-sm text-slate-300">
                      <span className="font-semibold text-white">
                        Recommended action:{" "}
                      </span>
                      {a.recommended_action}
                    </p>
                  )}
                  <div className="mt-3 text-xs text-slate-400">
                    Last updated {a.updated_at}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}
